import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { CaretLeft, CircleNotch, FloppyDisk, Image as ImageIcon, PaperPlaneTilt, Tag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MediaPicker } from "@/components/ui/media-picker";
import { VideoSourceField } from "./VideoSourceField";
import { VideoTagInput } from "./VideoTagInput";
import { isAutoDerivedPoster, parseVideoEmbed } from "@/lib/video-embed";
import type { VideoItem, VideoSourceType, VideoStatus } from "@/types";

export interface VideoFormValues {
  title: string;
  description: string;
  sourceType: VideoSourceType;
  videoUrl: string;
  posterUrl: string;
  tags: string[];
}

interface VideoFormProps {
  initial?: VideoItem;
  isSubmitting: boolean;
  onSubmit: (values: VideoFormValues, status: VideoStatus) => void;
}

export function VideoForm({ initial, isSubmitting, onSubmit }: VideoFormProps) {
  const [values, setValues] = useState<VideoFormValues>({
    title: initial?.title ?? "",
    description: initial?.description ?? "",
    sourceType: initial?.sourceType ?? "upload",
    videoUrl: initial?.videoUrl ?? "",
    posterUrl: initial?.posterUrl ?? "",
    tags: initial?.tags.map((t) => t.name) ?? [],
  });
  const patch = (p: Partial<VideoFormValues>) => setValues((v) => ({ ...v, ...p }));

  const sourceOk =
    values.sourceType === "upload"
      ? values.videoUrl.startsWith("/uploads/videos/")
      : parseVideoEmbed(values.videoUrl) !== null;
  const canSubmit = values.title.trim().length > 0 && sourceOk;
  const hint = !values.title.trim() ? "Unesite naslov" : !sourceOk ? "Dodajte video" : null;

  const actions = (full: boolean) => (
    <>
      <Button variant="outline" size={full ? "default" : "sm"} className={full ? "flex-1" : undefined} disabled={isSubmitting || !canSubmit} onClick={() => onSubmit(values, "draft")}>
        {isSubmitting ? <CircleNotch className="size-3.5 animate-spin" /> : <FloppyDisk className="size-3.5" />}
        Nacrt
      </Button>
      <Button size={full ? "default" : "sm"} className={full ? "flex-1" : undefined} disabled={isSubmitting || !canSubmit} onClick={() => onSubmit(values, "published")}>
        {isSubmitting ? <CircleNotch className="size-3.5 animate-spin" /> : <PaperPlaneTilt className="size-3.5" />}
        {initial?.status === "published" ? "Sačuvaj" : "Objavi"}
      </Button>
    </>
  );

  const sidebar = (
    <>
      <div className="space-y-2">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Tag className="size-3.5" />
          Tagovi
        </p>
        <VideoTagInput value={values.tags} onChange={(tags) => patch({ tags })} />
      </div>
      <div className="space-y-2">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <ImageIcon className="size-3.5" />
          Poster
        </p>
        <MediaPicker
          value={values.posterUrl}
          onChange={(posterUrl) => patch({ posterUrl })}
          folder="video-posters"
          hint="JPG, PNG ili WebP · maks. 10 MB"
          previewHeight="h-40"
        />
        <p className="text-xs leading-relaxed text-muted-foreground">
          Ako ostane prazno, za upload se kadar uzima automatski, a za YouTube se koristi njihova sličica.
        </p>
      </div>
    </>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex items-center justify-between sm:mb-8">
        <Link to="/dashboard/admin/videos" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <CaretLeft className="size-4" />
          Video
        </Link>
        <div className="hidden items-center gap-2 md:flex">{actions(false)}</div>
      </div>

      <div className="flex gap-8">
        <div className="min-w-0 flex-1 space-y-6">
          <input
            type="text"
            value={values.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Naslov videa..."
            className="w-full border-none bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30 sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          />
          {hint && <p className="-mt-3 text-xs text-amber-600">{hint}</p>}

          <VideoSourceField
            sourceType={values.sourceType}
            videoUrl={values.videoUrl}
            onChange={(next) =>
              // An auto-derived poster (YouTube thumbnail, ffmpeg frame) belongs to the old
              // source; drop it so the server derives a fresh one for the new source.
              setValues((v) => ({
                ...v,
                ...next,
                posterUrl:
                  (next.sourceType !== v.sourceType || next.videoUrl !== v.videoUrl) && isAutoDerivedPoster(v.posterUrl)
                    ? ""
                    : v.posterUrl,
              }))
            }
          />

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opis</p>
            <Textarea
              value={values.description}
              onChange={(e) => patch({ description: e.target.value })}
              rows={6}
              placeholder="O čemu je video, kome je namijenjen..."
            />
          </div>
        </div>

        <aside className="hidden w-72 shrink-0 md:block">
          <div className="sticky top-6 space-y-5">{sidebar}</div>
        </aside>
      </div>

      <div className="mt-8 space-y-5 md:hidden">
        <div className="h-px bg-border" />
        {sidebar}
        <div className="flex gap-2">{actions(true)}</div>
      </div>
    </div>
  );
}
